import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useWishlist } from '@/hooks/useWishlist';

interface Product {
  id: string;
  name: string;
  brand: string;
  rating: number;
  reviews_count: number;
  description: string;
  price: number;
  original_price: number | null;
  discount: number;
  image: string;
  badge: string;
  category: string;
}

const tabs = ['All', 'Electronics', 'Gaming', 'Home', 'Kitchen', 'Fitness', 'Beauty'];
const MAX_ITEMS = 8;

function Stars({ rating }: { rating: number }) {
  const full = Math.floor(rating || 0);
  const half = (rating || 0) - full >= 0.5;
  return (
    <span className="flex items-center gap-0.5 text-amber-400">
      {Array.from({ length: 5 }).map((_, i) => {
        const cls = i < full ? 'ri-star-fill' : i === full && half ? 'ri-star-half-line' : 'ri-star-line';
        return <i key={i} className={`${cls} text-xs`}></i>;
      })}
    </span>
  );
}

export default function ProductGrid() {
  const [active, setActive] = useState('All');
  const { isWishlisted, toggle: toggleWishlist } = useWishlist();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('products')
      .select('id,name,brand,rating,reviews_count,description,price,original_price,discount,image,badge,category')
      .order('rating', { ascending: false })
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) setError(true);
        else if (data) setProducts(data);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <section className="py-16 bg-ink-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 text-center text-ink-400 text-sm">Loading products...</div>
    </section>
  );

  if (error || products.length === 0) return null;

  const filtered = (active === 'All' ? products : products.filter((p) => p.category === active)).slice(0, MAX_ITEMS);

  return (
    <section id="products" className="py-16 md:py-20 bg-ink-50/50 border-b border-ink-100 reveal">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 tracking-tight">
              Top-rated this week
            </h2>
            <p className="mt-1.5 text-ink-500 text-sm">
              Hand-tested by our reviewers. Compared against 20+ competitors each.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setActive(t)}
                className={`h-8 px-3.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  active === t ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-600 hover:border-ink-300'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {filtered.map((p) => {
            const wishlisted = isWishlisted(p.id);
            const hasDiscount = p.original_price != null && p.original_price > p.price;
            return (
              <article key={p.id} className="group bg-white rounded-xl border border-ink-100 overflow-hidden flex flex-col hover:shadow-card hover:-translate-y-0.5 transition duration-200">
                <Link to={`/product/${p.id}`} className="relative aspect-square bg-ink-50 overflow-hidden block flex-shrink-0">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition duration-500"
                  />
                  {p.badge && (
                    <span className="absolute top-3 left-3 h-6 px-2 rounded-md bg-white border border-ink-100 text-ink-700 text-[10px] font-bold uppercase tracking-wider flex items-center shadow-soft">
                      {p.badge}
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(p.id); }}
                    className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                      wishlisted
                        ? 'bg-rose-50 text-rose-500 opacity-100'
                        : 'bg-white border border-ink-100 text-ink-400 opacity-0 group-hover:opacity-100'
                    }`}
                    aria-label={wishlisted ? 'Remove from wishlist' : 'Save'}
                  >
                    <i className={`${wishlisted ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                  </button>
                </Link>

                <div className="p-4 flex flex-col flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{p.brand}</p>
                  <Link
                    to={`/product/${p.id}`}
                    className="mt-1 font-semibold text-ink-900 text-sm leading-snug line-clamp-2 hover:text-cta-600 transition"
                  >
                    {p.name}
                  </Link>

                  <div className="mt-2 flex items-center gap-2">
                    <Stars rating={p.rating} />
                    <span className="text-xs text-ink-500">({(p.reviews_count || 0).toLocaleString()})</span>
                  </div>

                  <div className="mt-auto pt-4 flex items-end justify-between">
                    <div>
                      <p className="font-display text-lg font-bold text-ink-900">${p.price}</p>
                      {hasDiscount && (
                        <p className="text-xs text-ink-400 line-through">${p.original_price}</p>
                      )}
                    </div>
                    <Link
                      to={`/product/${p.id}`}
                      className="h-8 px-3 rounded-lg bg-ink-900 hover:bg-cta-600 text-white text-xs font-semibold whitespace-nowrap transition flex items-center gap-1"
                    >
                      View <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/search"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg border border-ink-200 bg-white text-sm font-semibold text-ink-700 hover:border-ink-300 hover:text-ink-900 transition"
          >
            Browse all products <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
